import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Camera, Send, CheckCircle2 } from "lucide-react";

const CATEGORIES = ["cleaning", "urban_furniture", "lighting", "signage", "security", "other"];

export default function Incidents() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    category: "cleaning", description: "", name: "", email: "",
    photo_url: "", lat: null, lng: null, address: "",
  });
  const [locating, setLocating] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const useGPS = () => {
    if (!navigator.geolocation) return toast.error(t("incidents.err_gps"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setLocating(false);
        toast.success(`${t("incidents.loc_success")}: ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
      },
      () => { setLocating(false); toast.error(t("incidents.err_location")); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error(t("incidents.err_photo_size"));
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error(t("incidents.err_desc_required"));
    try {
      await api.post("/incidents", form);
      setSubmitted(true);
      toast.success(t("incidents.success"));
    } catch {
      toast.error(t("incidents.err_submit"));
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="incident-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-3">{t("incidents.success")}</h1>
          <p className="text-muted-foreground">{t("incidents.success_sub")}</p>
          <Button className="mt-8 rounded-full" onClick={() => { setSubmitted(false); setForm({ ...form, description: "", photo_url: "" }); }} data-testid="incident-new-btn">
            {t("incidents.new_incident")}
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="overline text-accent">{t("incidents.overline")}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{t("incidents.title")}</h1>
          <p className="mt-3 text-muted-foreground">{t("incidents.subtitle")}</p>
        </div>

        <Card className="rounded-3xl border-2">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={submit} className="space-y-6" data-testid="incident-form">
              <div>
                <Label>{t("incidents.category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-2 rounded-xl" data-testid="incident-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{t(`incidents.categories.${c}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t("incidents.description")} *</Label>
                <Textarea
                  className="mt-2 rounded-xl min-h-28"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="incident-description-input"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("incidents.name")}</Label>
                  <Input className="mt-2 rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="incident-name-input" />
                </div>
                <div>
                  <Label>{t("incidents.email")}</Label>
                  <Input type="email" className="mt-2 rounded-xl" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="incident-email-input" />
                </div>
              </div>

              <div>
                <Label>{t("incidents.location")}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" className="rounded-full" onClick={useGPS} disabled={locating} data-testid="incident-gps-btn">
                    <MapPin className="w-4 h-4 mr-2" aria-hidden="true" />
                    {locating ? t("incidents.locating") : t("incidents.use_gps")}
                  </Button>
                  {form.lat && (
                    <span className="text-xs px-3 py-1.5 rounded-full bg-secondary self-center" data-testid="incident-coords">
                      {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                    </span>
                  )}
                </div>
                <Input placeholder={t("incidents.address_ph")} aria-label={t("incidents.address_ph")} className="mt-3 rounded-xl" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="incident-address-input" />
              </div>

              <div>
                <Label>{t("incidents.photo")}</Label>
                <div className="mt-2 flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 rounded-full border-2 border-border hover:border-accent transition-colors text-sm font-medium focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2" data-testid="incident-photo-label">
                    <Camera className="w-4 h-4 mr-2" aria-hidden="true" />
                    {t("incidents.add_photo")}
                    <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhoto} data-testid="incident-photo-input" aria-label={t("incidents.add_photo")} />
                  </label>
                  {form.photo_url && (
                    <img src={form.photo_url} alt="preview" className="w-14 h-14 rounded-xl object-cover border-2 border-border" data-testid="incident-photo-preview" />
                  )}
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full rounded-full h-12" data-testid="incident-submit-btn">
                <Send className="w-4 h-4 mr-2" /> {t("incidents.submit")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
